import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';

interface QueryOptions {
  single?: boolean;
  withRelations?: string;
  cache?: boolean;
  refetchOnMount?: boolean;
  refetchOnWindowFocus?: boolean;
}

interface QueryConfig {
  key: string;
  params?: Record<string, any>;
  options?: QueryOptions;
}

// Cache global pour les requêtes
const globalCache: Record<string, { data: any; timestamp: number }> = {};

export function useQueries(queries: QueryConfig[]) {
  const [results, setResults] = useState<Array<{ data: any; loading: boolean; error: any }>>(
    queries.map(() => ({ data: null, loading: true, error: null }))
  );
  
  // Utiliser des références pour éviter les re-rendus en boucle
  const fetchedRef = useRef(false);
  const queriesRef = useRef(queries);

  // Créer une clé stable pour les requêtes
  const queriesKey = JSON.stringify(queries.map(q => ({
    key: q.key,
    params: q.params,
    options: {
      single: q.options?.single,
      withRelations: q.options?.withRelations
    }
  })));

  useEffect(() => {
    // Mettre à jour la référence aux requêtes
    queriesRef.current = queries;
    
    // Eviter les requêtes multiples pour le même ensemble de requêtes
    if (fetchedRef.current) return;
    
    const fetchData = async () => {
      // Marquer comme déjà récupéré pour éviter les requêtes multiples
      fetchedRef.current = true;
      
      const newResults = await Promise.all(
        queriesRef.current.map(async (query, index) => {
          try {
            const { key, params = {}, options = {} } = query;
            const cacheKey = `${key}_${JSON.stringify(params)}`;
            
            // Vérifier le cache global
            if (options.cache && globalCache[cacheKey]) {
              const { data, timestamp } = globalCache[cacheKey];
              // Cache valide pendant 5 minutes
              if (Date.now() - timestamp < 5 * 60 * 1000) {
                return { data, loading: false, error: null };
              }
            }
            
            // Vérifier le cache local (localStorage)
            if (options.cache) {
              try {
                const cachedData = localStorage.getItem(`query_${cacheKey}`);
                if (cachedData) {
                  const { data, timestamp } = JSON.parse(cachedData);
                  // Cache valide pendant 5 minutes
                  if (Date.now() - timestamp < 5 * 60 * 1000) {
                    return { data, loading: false, error: null };
                  }
                }
              } catch (error) {
                console.error('Erreur de lecture du cache:', error);
              }
            }

            let request = supabase.from(key).select(options.withRelations || '*');

            // Appliquer les paramètres de filtrage
            Object.entries(params).forEach(([key, value]) => {
              if (value !== undefined) {
                request = request.eq(key, value);
              }
            });

            // Récupérer un seul élément ou tous
            const { data, error } = options.single
              ? await request.single()
              : await request;

            // Mettre en cache si activé
            if (options.cache && data) {
              // Cache en mémoire
              globalCache[cacheKey] = { data, timestamp: Date.now() };
              
              // Cache local
              try {
                localStorage.setItem(
                  `query_${cacheKey}`,
                  JSON.stringify({ data, timestamp: Date.now() })
                );
              } catch (error) {
                console.error('Erreur d\'écriture du cache:', error);
              }
            }

            return { data, loading: false, error };
          } catch (error) {
            return { data: null, loading: false, error };
          }
        })
      );

      setResults(newResults);
    };

    fetchData();

    // Nettoyage
    return () => {
      fetchedRef.current = false;
    };
  }, [queriesKey]);

  return results;
} 